import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { obtenerEstadoPago } from '../services/payloadService';
import './PaymentConfirmation.css';

/**
 * PaymentConfirmation - Página de confirmación de pago
 * Muestra el estado del pago y hace polling cada 3 segundos hasta que cambie
 */
const PaymentConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [payment, setPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(true);
  const [pollingCount, setPollingCount] = useState(0);

  // Get reference from URL - ePayco can send different parameters
  // Priority: ref (manually added by us, most reliable) > x_id_invoice > ref_payco
  const reference = searchParams.get('ref') || searchParams.get('x_id_invoice') || searchParams.get('ref_payco');

  // Máximo de intentos de polling (60 intentos × 3 segundos = 3 minutos)
  const MAX_POLLING_ATTEMPTS = 60;

  /**
   * Obtener estado del pago desde el backend
   */
  const fetchPaymentStatus = async () => {
    try {
      const response = await obtenerEstadoPago(reference);

      if (response.success && response.data) {
        setPayment(response.data);
        setError(null);

        // Detener polling si el estado ya no es pendiente
        if (response.data.estado !== 'pendiente') {
          setIsPolling(false);
        }
      }
    } catch (err) {
      console.error('Error al consultar estado:', err);
      setError('No se pudo verificar el estado del pago');
    } finally {
      setIsLoading(false);
    }
  };

  // Primer fetch al montar el componente
  useEffect(() => {
    if (!reference) {
      setError('No se proporcionó una referencia de pago');
      setIsLoading(false);
      return;
    }

    fetchPaymentStatus();
  }, [reference]);

  // Polling cada 3 segundos mientras el pago esté pendiente
  useEffect(() => {
    if (!isPolling || pollingCount >= MAX_POLLING_ATTEMPTS) {
      if (pollingCount >= MAX_POLLING_ATTEMPTS) {
        console.log('Máximo de intentos de polling alcanzado');
      }
      setIsPolling(false);
      return;
    }

    const interval = setInterval(() => {
      console.log(`Polling intento ${pollingCount + 1}/${MAX_POLLING_ATTEMPTS}`);
      fetchPaymentStatus();
      setPollingCount((prev) => prev + 1);
    }, 3000); // Cada 3 segundos

    return () => clearInterval(interval);
  }, [isPolling, pollingCount]);

  // Detener polling cuando el estado cambie
  useEffect(() => {
    if (payment && payment.estado !== 'pendiente') {
      setIsPolling(false);
    }
  }, [payment?.estado]);

  /**
   * Renderizar mensaje según estado
   */
  const renderStatusMessage = () => {
    if (!payment) return null;

    switch (payment.estado) {
      case 'aceptada':
        return (
          <div className="status-success">
            <div className="status-icon">✓</div>
            <h1>¡Pago Exitoso!</h1>
            <p>Tu pago ha sido procesado correctamente.</p>
            <div className="payment-details">
              <p><strong>Referencia:</strong> {payment.numeroOrden}</p>
              <p><strong>Monto:</strong> ${parseFloat(payment.total).toLocaleString('es-CO')} COP</p>
              {payment.approvalCode && (
                <p><strong>Código de aprobación:</strong> {payment.approvalCode}</p>
              )}
              {payment.transactionId && (
                <p><strong>ID de transacción:</strong> {payment.transactionId}</p>
              )}
            </div>
            <button onClick={() => navigate('/')} className="btn-primary">
              Volver al inicio
            </button>
          </div>
        );

      case 'rechazada':
      case 'fallida':
        return (
          <div className="status-failed">
            <div className="status-icon">✗</div>
            <h1>Pago Rechazado</h1>
            <p>No se pudo procesar tu pago.</p>
            <div className="payment-details">
              <p><strong>Referencia:</strong> {payment.numeroOrden}</p>
              {payment.responseText && (
                <p><strong>Motivo:</strong> {payment.responseText}</p>
              )}
            </div>
            <button onClick={() => navigate('/')} className="btn-secondary">
              Intentar nuevamente
            </button>
          </div>
        );

      case 'pendiente':
      case 'iniciada':
        return (
          <div className="status-pending">
            <div className="status-icon spinner">⟳</div>
            <h1>Procesando Pago...</h1>
            <p>Tu pago está siendo procesado. Por favor espera.</p>
            <div className="payment-details">
              <p><strong>Referencia:</strong> {payment.numeroOrden}</p>
              <p><strong>Monto:</strong> ${parseFloat(payment.total).toLocaleString('es-CO')} COP</p>
            </div>
            {isPolling && (
              <p className="polling-message">
                Actualizando estado automáticamente...
              </p>
            )}
          </div>
        );

      case 'cancelada':
      case 'abandonada':
        return (
          <div className="status-cancelled">
            <div className="status-icon">⊘</div>
            <h1>Pago Cancelado</h1>
            <p>El pago fue cancelado.</p>
            <div className="payment-details">
              <p><strong>Referencia:</strong> {payment.numeroOrden}</p>
            </div>
            <button onClick={() => navigate('/')} className="btn-secondary">
              Volver al inicio
            </button>
          </div>
        );

      default:
        return (
          <div className="status-unknown">
            <div className="status-icon">?</div>
            <h1>Estado Desconocido</h1>
            <p>No pudimos determinar el estado de tu pago.</p>
            <div className="payment-details">
              <p><strong>Referencia:</strong> {payment.numeroOrden}</p>
              <p><strong>Estado:</strong> {payment.estado}</p>
            </div>
            <button onClick={() => navigate('/')} className="btn-secondary">
              Volver al inicio
            </button>
          </div>
        );
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="payment-confirmation-container">
        <div className="loading">
          <div className="spinner">⟳</div>
          <p>Verificando estado del pago...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="payment-confirmation-container">
        <div className="status-error">
          <div className="status-icon">!</div>
          <h1>Error</h1>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-confirmation-container">
      {renderStatusMessage()}
    </div>
  );
};

export default PaymentConfirmation;
